import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  IconButton,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Chip,
  TextField,
  Button,
  Collapse,
  Paper,
} from '@mui/material';
import {
  Person as PersonIcon,
  SmartToy as BotIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import {
  ChatMessage,
  IncubationSession,
  WorkflowStepType,
  SystemNarrative,
  Epic,
  UserStory,
} from '../models/incubation';

interface MessageComponentProps {
  message: ChatMessage;
  session: IncubationSession;
  onEditNarrative?: () => void;
}

const MessageComponent: React.FC<MessageComponentProps> = ({ message, session, onEditNarrative }) => {
  const [editingNarrative, setEditingNarrative] = useState(false);
  const [editedNarrative, setEditedNarrative] = useState<SystemNarrative | string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const handleEditNarrative = () => {
    if (message.metadata?.isGeneratedNarrative) {
      setEditedNarrative(message.metadata.rawNarrative);
      setEditingNarrative(true);
    } else if (session.systemNarrative) {
      setEditedNarrative(session.systemNarrative);
      setEditingNarrative(true);
    }
  };

  const saveNarrativeEdits = () => {
    if (editedNarrative) {
      // Update the message content with the edited narrative
      if (message.metadata?.isGeneratedNarrative) {
        message.content = `
          <h3>System Narrative (Edited)</h3>
          <p>${editedNarrative}</p>
        `;
        message.metadata.rawNarrative = editedNarrative as string;
        delete message.metadata.isGeneratedNarrative; // Remove the flag after editing
      }
      
      session.systemNarrative = editedNarrative;
      setEditingNarrative(false);
      
      // Notify parent component if callback is provided
      if (onEditNarrative) {
        onEditNarrative();
      }
    }
  };

  const cancelNarrativeEdit = () => {
    setEditingNarrative(false);
    setEditedNarrative(null);
  };

  const renderSystemNarrative = () => {
    // If this is the initial generated narrative that needs to be editable
    if (message.metadata?.isGeneratedNarrative) {
      if (editingNarrative) {
        return (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="System Narrative"
              multiline
              rows={10}
              value={editedNarrative || message.metadata.rawNarrative}
              onChange={(e) => setEditedNarrative(e.target.value)}
              sx={{ mb: 2 }}
              helperText="Edit the generated narrative to better match your vision"
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button variant="contained" size="small" startIcon={<SaveIcon />} onClick={saveNarrativeEdits}>
                Save
              </Button>
              <Button variant="outlined" size="small" startIcon={<CancelIcon />} onClick={cancelNarrativeEdit}>
                Cancel
              </Button>
            </Box>
          </Box>
        );
      }
      
      // Show the narrative with the edit option
      return <Typography variant="body1" dangerouslySetInnerHTML={{ __html: message.content }} />;
    }
    
    // If the systemNarrative is a string, render it as HTML content
    if (typeof session.systemNarrative === 'string') {
      return <Typography variant="body1" dangerouslySetInnerHTML={{ __html: message.content }} />;
    }
    
    if (!session.systemNarrative) return null;

    if (editingNarrative && editedNarrative && typeof editedNarrative !== 'string') {
      return (
        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="Vision"
            multiline
            rows={3}
            value={editedNarrative.systemVision}
            onChange={(e) => setEditedNarrative({ ...editedNarrative, systemVision: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Description"
            multiline
            rows={4}
            value={editedNarrative.systemDescription}
            onChange={(e) => setEditedNarrative({ ...editedNarrative, systemDescription: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Core Purpose"
            multiline
            rows={3}
            value={editedNarrative.corePurpose}
            onChange={(e) => setEditedNarrative({ ...editedNarrative, corePurpose: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Target User"
            value={editedNarrative.targetUser}
            onChange={(e) => setEditedNarrative({ ...editedNarrative, targetUser: e.target.value })}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button variant="contained" size="small" startIcon={<SaveIcon />} onClick={saveNarrativeEdits}>
              Save
            </Button>
            <Button variant="outlined" size="small" startIcon={<CancelIcon />} onClick={cancelNarrativeEdit}>
              Cancel
            </Button>
          </Box>
        </Box>
      );
    }

    const narrative = session.systemNarrative;
    return (
      <Paper sx={{ p: 2, mt: 2, backgroundColor: 'grey.50' }}>
        <Typography variant="h6" gutterBottom>System Narrative</Typography>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="primary">Vision:</Typography>
          <Typography variant="body2">{narrative.systemVision}</Typography>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="primary">Description:</Typography>
          <Typography variant="body2">{narrative.systemDescription}</Typography>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="primary">Core Purpose:</Typography>
          <Typography variant="body2">{narrative.corePurpose}</Typography>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="primary">Target User:</Typography>
          <Typography variant="body2">{narrative.targetUser}</Typography>
        </Box>
        
        {/* Expandable sections */}
        <Box>
          <Box
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: 1 }}
            onClick={() => toggleSection('keyFeatures')}
          >
            <Typography variant="subtitle2" color="primary">Key Features</Typography>
            <IconButton size="small">
              {expandedSections.has('keyFeatures') ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={expandedSections.has('keyFeatures')}>
            <Box sx={{ pl: 2, mb: 2 }}>
              {narrative.keyFeatures.map((feature, index) => (
                <Typography key={index} variant="body2">• {feature}</Typography>
              ))}
            </Box>
          </Collapse>
        </Box>
      </Paper>
    );
  };

  const renderSystemRequirements = () => {
    if (!session.systemRequirements) return null;

    return (
      <Paper sx={{ p: 2, mt: 2, backgroundColor: 'grey.50' }}>
        <Typography variant="h6" gutterBottom>System Requirements</Typography>
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>Epics</Typography>
          <FormGroup>
            {session.systemRequirements.epics.map((epic: Epic) => (
              <FormControlLabel
                key={epic.id}
                control={<Checkbox defaultChecked />}
                label={`${epic.title} - ${epic.description}`}
              />
            ))}
          </FormGroup>
        </Box>
        
        <Box>
          <Typography variant="subtitle1" gutterBottom>User Stories</Typography>
          <FormGroup>
            {session.systemRequirements.userStories.map((story: UserStory) => (
              <FormControlLabel
                key={story.id}
                control={<Checkbox defaultChecked />}
                label={story.storyText}
              />
            ))}
          </FormGroup>
        </Box>
      </Paper>
    );
  };

  const renderMessageContent = () => {
    // Parse HTML content (in production, use a proper HTML sanitizer)
    if (message.stepType === WorkflowStepType.SystemNarrative) {
      return renderSystemNarrative();
    } else if (message.stepType === WorkflowStepType.SystemRequirements) {
      return renderSystemRequirements();
    } else {
      return <Typography variant="body1" dangerouslySetInnerHTML={{ __html: message.content }} />;
    }
  };

  return (
    <Box sx={{ mb: 3, display: 'flex', justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start' }}>
      <Box sx={{ display: 'flex', gap: 1, maxWidth: '80%' }}>
        {message.role === 'assistant' && (
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <BotIcon />
          </Avatar>
        )}
        
        <Card sx={message.role === 'user' ? { bgcolor: 'primary.main', color: 'white' } : {}}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="caption" sx={{ mr: 2 }}>
                {message.role === 'user' ? 'You' : 'AI Assistant'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {new Date(message.timestamp).toLocaleTimeString()}
              </Typography>
              {message.role === 'assistant' && message.stepType === WorkflowStepType.SystemNarrative && (
                <IconButton size="small" onClick={handleEditNarrative} sx={{ ml: 'auto' }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            
            {renderMessageContent()}
          </CardContent>
        </Card>
        
        {message.role === 'user' && (
          <Avatar sx={{ bgcolor: 'secondary.main' }}>
            <PersonIcon />
          </Avatar>
        )}
      </Box>
    </Box>
  );
};

export default MessageComponent;